// Ad-click attribution capture.
//
// Google Ads appends a `gclid` to the landing URL when auto-tagging is on. The
// Google tag normally stashes it in a first-party cookie, but that cookie is
// capped at 7 days by Safari's ITP and cleared by ad blockers — so a purchase
// weeks after the click goes unattributed. We capture the click id ourselves at
// landing, persist it through signup onto the UserProfile, and report the
// conversion server-side when Stripe confirms payment. See users/models.py.
//
// Google's offline conversion import only accepts clicks up to 90 days old, so
// there is no point holding a gclid longer than that.
const STORAGE_KEY = 'algovex_attribution';
const MAX_AGE_DAYS = 90;

const TRACKED_PARAMS = ['gclid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

// Read from localStorage, dropping anything past the import window.
function read() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    const ageMs = Date.now() - new Date(data.captured_at).getTime();
    if (!Number.isFinite(ageMs) || ageMs > MAX_AGE_DAYS * 24 * 60 * 60 * 1000) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

// Capture click params from the current URL. Call once on app boot.
//
// Last-click wins: a fresh gclid overwrites a stored one, matching how Google
// attributes the conversion itself. A visit with no click params leaves any
// stored attribution alone, so navigating the site doesn't wipe the referral.
export function captureAttribution() {
  try {
    const params = new URLSearchParams(window.location.search);
    const captured = {};
    for (const key of TRACKED_PARAMS) {
      const value = params.get(key);
      if (value) captured[key] = value.slice(0, 255);
    }
    if (Object.keys(captured).length === 0) return read();

    const data = { ...captured, captured_at: new Date().toISOString(), landing_page: window.location.pathname };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    return data;
  } catch {
    /* private mode / storage disabled — attribution is best-effort, never fatal */
    return null;
  }
}

// Attribution payload for the register call, or null when this user arrived organically.
export function getAttribution() {
  return read();
}
